import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ApiUrl } from 'Services/Services';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

function EditBranch() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [radius, setRadius] = useState(1);
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    address: '',
  });

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstance = useRef(null);
  const geocoder = useRef(null);
  const circleRef = useRef(null);
  const autocompleteInputRef = useRef(null);

  useEffect(() => {
    const FetchBranch = async () => {
      try {
        const response = await axios.get(`${ApiUrl}/branches/${id}`);
        const branch = response.data.data;
        setName(branch.name);
        setPhoneNumber(branch.phoneNumber);
        setRadius(branch.radius);
        setLocation({
          latitude: parseFloat(branch.latitude),
          longitude: parseFloat(branch.longitude),
          address: branch.address,
        });
      } catch (err) {
        toast.error('Failed to fetch branch data.');
        console.error(err);
      }
    };
    FetchBranch();
  }, [id]);

  useEffect(() => {
    if (window.google) {
      initMap();
    }
  }, [location.latitude, location.longitude]);

  const initMap = () => {
    geocoder.current = new window.google.maps.Geocoder();
    const pos = {
      lat: location.latitude || 31.5204,
      lng: location.longitude || 74.3587,
    };

    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center: pos,
      zoom: 15,
    });

    const autocomplete = new window.google.maps.places.Autocomplete(
      autocompleteInputRef.current,
      { types: ['geocode'] }
    );

    autocomplete.bindTo('bounds', mapInstance.current);

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      mapInstance.current.panTo({ lat, lng });
      setLocation({
        latitude: lat,
        longitude: lng,
        address: place.formatted_address || place.name,
      });
      placeMarker(lat, lng);
    });

    mapInstance.current.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setLocation((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));
      placeMarker(lat, lng);
      getAddress(lat, lng);
    });

    placeMarker(pos.lat, pos.lng);
  };

  const getAddress = (lat, lng) => {
    geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setLocation((prev) => ({
          ...prev,
          address: results[0].formatted_address,
        }));
      }
    });
  };

  const placeMarker = (lat, lng) => {
    if (markerRef.current) markerRef.current.setMap(null);

    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstance.current,
    });

    drawCircle(lat, lng, radius);
  };

  const drawCircle = (lat, lng, rad) => {
    if (circleRef.current) circleRef.current.setMap(null);

    circleRef.current = new window.google.maps.Circle({
      strokeColor: '#3b82f6',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#3b82f6',
      fillOpacity: 0.2,
      map: mapInstance.current,
      center: { lat, lng },
      radius: rad * 1000,
    });
  };

  useEffect(() => {
    if (location.latitude && location.longitude) {
      drawCircle(location.latitude, location.longitude, radius);
    }
  }, [radius]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name,
        phoneNumber,
        radius,
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
      };

      await axios.put(`${ApiUrl}/branches/${id}`, payload);
      toast.success('Branch updated successfully');
      navigate('/admin/branches'); // redirect to list or view page
    } catch (err) {
      toast.error('Failed to update branch');
      console.error(err);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-xl p-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Edit Branch</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Branch Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md px-4 py-2"
              placeholder="e.g., Food Republic - Gulberg"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md px-4 py-2"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Location</label>
            <input
              type="text"
              ref={autocompleteInputRef}
              placeholder="Search for a place"
              className="w-full mb-3 border border-gray-300 rounded-md px-4 py-2"
            />
            <div ref={mapRef} className="w-full h-96 rounded-lg border border-gray-300"></div>
            {location.address && (
              <p className="text-sm text-gray-600 mt-2">
                📍 <strong>Selected Address:</strong> {location.address}
              </p>
            )}
          </div>

          {/* Radius */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Radius (km)</label>
            <input
              type="number"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              min={1}
              required
              className="w-full border border-gray-300 rounded-md px-4 py-2"
            />
          </div>

          {/* Save */}
          <div className="pt-2">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-md transition duration-200"
            >
              Update Branch
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBranch;
